import { ZodAny } from "zod";

import UserProperty from "@repo/kikoff-models/UserProperty";
import canadaFrontendRpc from "@repo/protobuf/api/canadaFrontendRpc";
import UFunction from "@repo/utils/UFunction";
import FeatureStore from "@repo/utils-solid/FeatureStore";

const initialState = {
  byName: {} as {
    [Category in UserProperty.Category]?: {
      [Name in UserProperty.NameByCategory[Category]]?: UserProperty.Value<
        Category,
        Name
      > | null;
    };
  },
};

export const { Provider, useUserProperties, Context } = FeatureStore.init(
  "UserProperties",
  initialState,
  ([store, setStore], { withLogger, attachAction }) => {
    const accessors = {
      valueOf: attachAction(
        <
          Category extends UserProperty.Category,
          Name extends UserProperty.NameByCategory[Category],
        >(
          category: Category,
          name: Name
        ) =>
          store.byName[category]?.[name] as NonNullable<
            (typeof store.byName)[Category]
          >[Name],
        (name, category) => actions.fetch(name, category)
      ),
    };
    const mutations = {
      update<
        Category extends UserProperty.Category,
        Name extends UserProperty.NameByCategory[Category],
      >(
        category: Category,
        name: Name,
        value: UserProperty.Value<Category, Name>
      ) {
        setStore("byName", category, { [name]: value });
      },
      clear(category: UserProperty.Category) {
        // eslint-disable-next-line no-unused-vars
        setStore("byName", ({ [category]: _, ...res }) => res);
      },
    };

    const us = {
      actions: {
        fetch: UFunction.todo,
        write: UFunction.todo,
        clear: UFunction.todo,
      },
    };

    const canada = {
      actions: {
        fetch<
          Category extends UserProperty.Category,
          Name extends UserProperty.NameByCategory[Category],
        >(category: Category, name: Name) {
          return canadaFrontendRpc.Storage.read({
            name: name as string,
            category,
          }).then(({ value }) => {
            const schema = UserProperty.validate[category][name] as ZodAny;
            mutations.update(
              category,
              name,
              (value && schema.safeParse(JSON.parse(value)).data) ?? null
            );
          });
        },
        write<
          Category extends UserProperty.Category,
          Name extends UserProperty.NameByCategory[Category],
        >(
          category: Category,
          name: Name,
          value: UserProperty.Value<Category, Name>
        ) {
          return canadaFrontendRpc.Storage.write({
            category,
            name: name as string,
            value: JSON.stringify(value),
          }).then(() => mutations.update(category, name, value));
        },
        clear(category: UserProperty.Category) {
          return canadaFrontendRpc.Storage.clear({ category }).then(() =>
            mutations.clear(category)
          );
        },
      },
    };

    const actions = withLogger("action", {
      ...(import.meta.env.API_ADAPTER === "canada"
        ? canada.actions
        : us.actions),
    });
    return { accessors, actions };
  }
);
